import type { HomeworkSource, OgeTopicPick } from "@edu/shared";
import {
  TOPIC_SUBJECTS,
  isForeignTopics,
  materialLine,
  subjectBy,
  subjectWord,
  type TopicSubject,
} from "../../exercises/topics/subject.ts";

/**
 * Предметы формы выдачи: какой источник заданий каким словом называется и как
 * читается уровень материала у темы (раздел 7, пункт 15
 * `docs/english-generators-design.md`).
 *
 * Отдельным модулем от разметки по правилу раздела 9 CLAUDE.md: тестов на
 * компоненты в проекте нет, а слова, которые видит преподаватель, проверить
 * надо, и проверяются они только тогда, когда живут в чистой функции.
 *
 * ⚠️ Словарь предметов переехал в `features/exercises/topics/subject.ts`
 * 03.09.2026: те же слова понадобились второму экрану, комнате урока, а
 * импортировать их туда ИЗ ФОРМЫ значило бы расширить поверхность, которую
 * приходится проверять руками замком барьера. Здесь осталось то, что про
 * домашку и только про неё: виды источника, заказ и подписи радиокнопок.
 * Реэкспорт ниже оставлен затем, чтобы пять файлов формы не переучивались на
 * новый адрес заодно с правкой, которая их не касается (тот же приём и та же
 * причина, что у `guessTimeZone` в `features/schedule/common.ts`).
 */
export { TOPIC_SUBJECTS, isForeignTopics, materialLine, subjectBy };
export type { TopicSubject };

/**
 * Вид источника заданий в форме.
 *
 * ⚠️ Берётся из контракта, а не переписывается рядом: до английской волны здесь
 * стоял свой юнион `"plan" | "oge"`, и появление третьего члена у
 * `HomeworkSource` уронило форму двумя ошибками типов вместо того, чтобы просто
 * доехать. Теперь четвёртый источник в контракте сломает ровно те места, где о
 * нём надо принять решение (карты ниже), а не случайные две строки.
 */
export type SourceKind = HomeworkSource["kind"];

/** Источник, собираемый генераторами: у него есть предмет и справочник тем. */
export type TopicSource = Exclude<SourceKind, "plan">;

/**
 * Карты, а не `switch`: пропущенный предмет здесь это ошибка компиляции, а
 * пропущенная ветка `switch` без `noImplicitReturns` молча вернула бы
 * `undefined`.
 */
const SOURCE_OF: Record<TopicSubject, TopicSource> = { math: "oge", english: "eng" };
const SUBJECT_OF_TOPIC: Record<TopicSource, TopicSubject> = { oge: "math", eng: "english" };
const SUBJECT_OF: Record<SourceKind, TopicSubject | null> = { plan: null, ...SUBJECT_OF_TOPIC };

export function sourceOfSubject(subject: TopicSubject): TopicSource {
  return SOURCE_OF[subject];
}

/** Предмет источника. `null` у заготовки: у неё предмета нет вовсе. */
export function subjectOfSource(kind: SourceKind): TopicSubject | null {
  return SUBJECT_OF[kind];
}

/** Предмет генераторного источника: здесь он есть всегда, и `null` не бывает. */
export function subjectOfTopicSource(kind: TopicSource): TopicSubject {
  return SUBJECT_OF_TOPIC[kind];
}

/** Подпись радиокнопки: «По темам: английский». */
export function sourceLabel(subject: TopicSubject): string {
  return `По темам: ${subjectWord(subject)}`;
}

/**
 * Заказ по темам под выбранный источник.
 *
 * Функцией, а не литералом в форме, ровно затем, чтобы дискриминант выбирался
 * ОДИН раз и проверялся тестом: скопированная ветка с забытым `kind` отправила
 * бы английские слаги в математический реестр, и отказом это выглядело бы
 * только у ученика.
 */
export function topicOrder(kind: TopicSource, picks: OgeTopicPick[]): HomeworkSource {
  return kind === "eng" ? { kind: "eng", picks } : { kind: "oge", picks };
}

/**
 * Справочник тем не загрузился: какими словами сказать об этом в ветке «Из
 * заготовки», где выключенную радиокнопку иначе нечем объяснить.
 *
 * ⚠️ Про платформу, а не про преподавателя (раздел 5.10 CLAUDE.md): сломался
 * наш запрос, и это единственное, о чём здесь говорится.
 */
export function brokenTopicsNote(subjects: readonly TopicSubject[]): string | null {
  if (subjects.length === 0) return null;
  const list = subjects.map(subjectBy).join(" и ");
  return `Темы ${list} не загрузились, выбрать их пока нельзя.`;
}

/**
 * Справочник приехал ПУСТЫМ: генераторов по этому предмету у нас ещё нет.
 *
 * Вторая причина выключенной радиокнопки, и от первой она отличается тем, что
 * повторять нечего: кнопки «Повторить» рядом с этой строкой нет. Сегодня случай
 * недостижим (генераторы есть у обоих предметов), а с приходом третьего
 * предмета станет обычным, и выключенный контрол обязан объяснять себя раньше,
 * чем это увидит человек.
 *
 * ⚠️ Про платформу, а не про преподавателя (раздел 5.10 CLAUDE.md): чего нет у
 * нас, а не чего не сделал он.
 */
export function emptyTopicsNote(subjects: readonly TopicSubject[]): string | null {
  if (subjects.length === 0) return null;
  const list = subjects.map(subjectBy).join(" и ");
  return `Тем ${list} пока нет, задания берутся только из заготовки.`;
}
